import { useEffect, useRef, useState } from "react";

// Filing an incident, as a dialog rather than a page. A report is something
// that happens in the middle of doing something else — the modal keeps the
// leader where they were.
//
// Photos are downscaled in the browser before upload. A phone camera frame is
// several megabytes and the detail that matters in a damage photo survives
// 1600px comfortably.
const MAX_FILES = 6;
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.72;

function downscale(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("could not read that file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("that file is not an image"));
      img.onload = () => {
        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const today = () => new Date().toISOString().slice(0, 10);

export default function IncidentModal({ onClose, reporter, canRead = true }) {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    occurredOn: today(),
    category: "",
    affected: "",
    involved: "",
    description: "",
    escalated: null,
    escalatedTo: "",
    notEscalatedReason: "",
  });
  const [photos, setPhotos] = useState([]);
  const [people, setPeople] = useState([]);
  const [coReporters, setCoReporters] = useState([]);
  const [hunt, setHunt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filed, setFiled] = useState(null);
  const panel = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    fetch("/api/incidents/categories")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setCategories(d?.categories || []))
      .catch(() => setError("Could not load the category list."));

    // Who can be named alongside you. Names and keys only.
    fetch("/api/incidents/people")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setPeople(d?.people || []))
      .catch(() => {});
  }, []);

  // Escape closes, unless a submit is in flight — losing a half-written
  // safeguarding report to a stray keystroke is not a small thing.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const addPhotos = async (list) => {
    setError("");
    const room = MAX_FILES - photos.length;
    const chosen = [...list].slice(0, Math.max(0, room));
    try {
      const next = [];
      for (const file of chosen) next.push(await downscale(file));
      setPhotos((p) => [...p, ...next]);
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  const submit = async () => {
    setError("");
    if (!form.category) return setError("Pick what kind of incident this was.");
    if (form.description.trim().length < 20) {
      return setError("Describe what happened in a little more detail.");
    }
    if (form.escalated === null) {
      return setError("Say whether this was escalated to an authority.");
    }
    if (form.escalated && !form.escalatedTo.trim()) {
      return setError("Name the authority this was escalated to.");
    }
    if (!form.escalated && !form.notEscalatedReason.trim()) {
      return setError("Say why this was not escalated.");
    }

    setBusy(true);
    try {
      const r = await fetch("/api/incidents", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...form, files: photos, coReporters }),
      });
      const d = await r.json();
      if (!r.ok) setError(d.error || "Could not file the report.");
      else setFiled(d.id);
    } catch {
      setError("Could not file the report.");
    }
    setBusy(false);
  };

  if (filed) {
    return (
      <div className="mdl" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className="mdl-panel narrow" ref={panel}>
          <div className="mdl-head">
            <h2>Report filed.</h2>
          </div>
          <div className="mdl-body">
            <p className="mdl-p">
              Reference <b>{filed}</b>. It has gone to the senior pastor, who will
              acknowledge it.
            </p>
            {canRead ? (
              <p className="mdl-p">
                You and anyone you named as a reporter can open it again under{" "}
                <b>Incident reports</b>. Nobody else below senior level can.
              </p>
            ) : (
              <p className="mdl-p">
                Your name is on the record, but this is not something you can open
                again — reports are only readable by leadership. Write the reference
                down if you need to refer to it, or ask a leader.
              </p>
            )}
            <div className="mdl-note">
              If anyone is in immediate danger, this form is not the response. Call the
              authorities first.
            </div>
          </div>
          <div className="mdl-foot">
            <span />
            <button className="btn-post" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mdl" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="mdl-panel" ref={panel} role="dialog" aria-modal="true" aria-label="File an incident report">
        <div className="mdl-head">
          <div>
            <div className="mdl-eyebrow">CLLXTV · The Room</div>
            <h2>File an incident report.</h2>
          </div>
          <button className="mdl-x" onClick={onClose} disabled={busy} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="mdl-body">
          <div className="mdl-note">
            If anyone is in immediate danger, call the authorities first and file this
            afterwards.
          </div>

          <div className="mdl-row2">
            <label className="fld">
              <span className="flab">
                Date of the incident <b>*</b>
              </span>
              <input
                className="finput"
                type="date"
                max={today()}
                value={form.occurredOn}
                onChange={(e) => set("occurredOn", e.target.value)}
              />
            </label>

            <label className="fld">
              <span className="flab">
                What kind of incident <b>*</b>
              </span>
              <select
                className="finput"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
              >
                <option value="">Choose one…</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="fld">
            <span className="flab">Reported by</span>
            <input className="finput" value={reporter} readOnly />
            <span className="fhint">
              Taken from your sign-in, so a report always carries who filed it.
            </span>
          </label>

          <div className="fld">
            <span className="flab">
              {canRead ? "Anyone else reporting this" : "Who in leadership should see this"}
            </span>
            {!canRead && (
              <span className="fhint" style={{ marginBottom: 8, display: "block" }}>
                The senior pastor sees every report. Naming the leader over your area
                as well is how this reaches them directly.
              </span>
            )}
            {coReporters.length > 0 && (
              <div className="mdl-picked">
                {coReporters.map((r) => (
                  <span className="mdl-pill" key={r.key || r.name}>
                    {r.name}
                    {!r.key && <i> · not on the app</i>}
                    <button
                      onClick={() =>
                        setCoReporters((c) => c.filter((x) => (x.key || x.name) !== (r.key || r.name)))
                      }
                      aria-label={`Remove ${r.name}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              className="finput"
              value={hunt}
              onChange={(e) => setHunt(e.target.value)}
              placeholder="Start typing a name…"
            />
            {hunt.trim().length > 0 && (
              <div className="mdl-hits">
                {people
                  .filter(
                    (p) =>
                      p.name.toLowerCase().includes(hunt.trim().toLowerCase()) &&
                      !coReporters.some((c) => c.key === p.key),
                  )
                  .slice(0, 6)
                  .map((p) => (
                    <button
                      key={p.key}
                      className="mdl-hit"
                      onClick={() => {
                        setCoReporters((c) => [...c, p]);
                        setHunt("");
                      }}
                    >
                      {p.name}
                      <i>on the app</i>
                    </button>
                  ))}
                <button
                  className="mdl-hit"
                  onClick={() => {
                    setCoReporters((c) => [...c, { key: "", name: hunt.trim() }]);
                    setHunt("");
                  }}
                >
                  Add “{hunt.trim()}”
                  <i>not on the app</i>
                </button>
              </div>
            )}
            <span className="fhint">
              Anyone on the app you name here can read this report back. Someone not on
              the app is recorded by name only — there is no account to give access to.
            </span>
          </div>

          <div className="mdl-row2">
            <label className="fld">
              <span className="flab">Who it happened to</span>
              <input
                className="finput"
                value={form.affected}
                onChange={(e) => set("affected", e.target.value)}
                placeholder="Name, or a description if unknown"
              />
            </label>
            <label className="fld">
              <span className="flab">Who it came from</span>
              <input
                className="finput"
                value={form.involved}
                onChange={(e) => set("involved", e.target.value)}
                placeholder="Name, or 'unknown'"
              />
            </label>
          </div>

          <label className="fld">
            <span className="flab">
              What happened <b>*</b>
            </span>
            <textarea
              className="finput ftall"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Times, places, what was said and done, who else saw it. Write what you observed rather than what you concluded."
            />
          </label>

          <div className="fld">
            <span className="flab">Photos</span>
            <div className="mdl-photos">
              {photos.map((src, i) => (
                <div className="mdl-thumb" key={i}>
                  <img src={src} alt={`Attachment ${i + 1}`} />
                  <button
                    onClick={() => setPhotos((p) => p.filter((_, n) => n !== i))}
                    aria-label={`Remove attachment ${i + 1}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {photos.length < MAX_FILES && (
                <label className="mdl-add">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => addPhotos(e.target.files)}
                  />
                  <span>+ Add</span>
                </label>
              )}
            </div>
            <span className="fhint">
              Up to {MAX_FILES}. Shrunk on this device before upload, so a phone photo
              does not have to travel full size.
            </span>
          </div>

          <div className="fld">
            <span className="flab">
              Was this escalated to an authority outside the church? <b>*</b>
            </span>
            <div className="chips" style={{ marginTop: 8 }}>
              <button
                className={"chip" + (form.escalated === true ? " on" : "")}
                onClick={() => set("escalated", true)}
              >
                Yes
              </button>
              <button
                className={"chip" + (form.escalated === false ? " on" : "")}
                onClick={() => set("escalated", false)}
              >
                No
              </button>
            </div>
          </div>

          {form.escalated === true && (
            <label className="fld">
              <span className="flab">
                Who it was escalated to <b>*</b>
              </span>
              <textarea
                className="finput"
                value={form.escalatedTo}
                onChange={(e) => set("escalatedTo", e.target.value)}
                placeholder="Police, CPS, EMS, insurance — who, when, and any case or report number."
              />
            </label>
          )}

          {form.escalated === false && (
            <label className="fld">
              <span className="flab">
                Why it was not escalated <b>*</b>
              </span>
              <textarea
                className="finput"
                value={form.notEscalatedReason}
                onChange={(e) => set("notEscalatedReason", e.target.value)}
                placeholder="The reasoning, and who was part of that decision."
              />
            </label>
          )}

          {error && <div className="mdl-error">{error}</div>}
        </div>

        <div className="mdl-foot">
          <span className="mdl-priv">
            Readable by senior and the leaders named here
            {canRead ? "." : " — not by you, once it is filed."}
          </span>
          <div className="mdl-actions">
            <button className="chip" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button className="btn-post" onClick={submit} disabled={busy}>
              {busy ? "Filing…" : "File report"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
